import { CommandBar, ICommandBarItemProps, IContextualMenuItem, DayOfWeek } from "office-ui-fabric-react";
import React, { useMemo } from "react";
import { connect } from "react-redux";
import { Field } from "../../../../entities/Metadata";
import { CostType, EntityType } from "../../../../entities/common";
import { ApplicationState } from "../../../../store";
import {
    CreateTimeTrackingEntry,
    IsEnterpriseTimeTracker,
    TimeTrackingEntry,
    TimeTrackingEntryAttr,
    TimeTrackingEntrySourceType,
    TimeTrackingLine
} from "../../../../store/TimeTrackingStore";
import { nameof } from "../../../../store/services/metadataService";
import { DisplayField } from "../../../common/DisplayField";
import EntityName from "../../../views/list/columns/EntityName";
import TaskIcon from "../../../task/TaskIcon";
import TimeTrackingAdministrativeCategoryEntityName from "../../TimeTrackingAdministrativeCategoryEntityName";
import { IsEmptyObject, notUndefined, toDateTime } from "../../../../components/utils/common";
import { getDaysBetweenDates } from "../../../common/timeline/utils";
import { TimeTrackingGlobalSettings } from "../../../../store/Tenant";
import { ITimelineSegment } from "../../../common/timeline/TimelineSegment";
import {
    TimeReportingPeriod,
    isDateInClosedPeriod,
    isPeriodOpenForReporting
} from "../../../../store/TimeReportingPeriodsStore";
import { CalendarDataSet } from "../../../../store/calendar/common";
import { buildCloneToDateMenuItem } from "../../TimeTrackingCloneToDateMenu";
import { TimeTrackingReadonly } from "../../common";

type OwnProps = {
    timeTrackingLine: TimeTrackingLine;
    segment: ITimelineSegment;
    readonly: TimeTrackingReadonly;

    onCreate: () => void;
    onStartEditing: (entry: TimeTrackingEntry) => void;
    onClone: (entries: CreateTimeTrackingEntry[]) => void;
    onEditCompleted: (id: string, attributes: Partial<TimeTrackingEntryAttr>) => void;

    resourceCalendarDataSet: CalendarDataSet;
};

type StoreProps = {
    fields: Field[];
    timeTrackingSettings: TimeTrackingGlobalSettings;
    isEnterpriseTimeTracker: boolean;
    periods: TimeReportingPeriod[];
    shownPeriod: TimeReportingPeriod | null;
};

type Props = OwnProps & StoreProps;

const _hiddenFields: string[] = [
    nameof<TimeTrackingEntryAttr>("Project"),
    nameof<TimeTrackingEntryAttr>("Task"),
    nameof<TimeTrackingEntryAttr>("Resource"),
    nameof<TimeTrackingEntryAttr>("CustomTimeEntryName"),
    nameof<TimeTrackingEntryAttr>("AdministrativeCategory"),
    nameof<TimeTrackingEntryAttr>("Date"),
    nameof<TimeTrackingEntryAttr>("Duration")
];

function TimeTrackingCellTooltip(props: Props) {
    const {
        timeTrackingLine,
        segment,
        fields,
        onCreate,
        onStartEditing,
        onClone,
        onEditCompleted,
        resourceCalendarDataSet,
        timeTrackingSettings,
        readonly,
        isEnterpriseTimeTracker,
        periods,
        shownPeriod
    } = props;

    const title = timeTrackingLine.attributes.Task.name;
    const entry = timeTrackingLine.TimeEntries.filter(
        _ => _.attributes.Date >= segment.startDate && _.attributes.Date <= segment.finishDate
    )[0];

    const isLocked = useMemo(() => isDateInClosedPeriod(segment.finishDate, periods), [segment.finishDate, periods]);

    const visibleFields = entry?.attributes.Duration
        ? fields.filter(field => {
              if (_hiddenFields.includes(field.name)) {
                  return false;
              }

              return (
                  field.name === nameof<TimeTrackingEntryAttr>("Description") ||
                  isFieldHasValue(entry.attributes[field.name])
              );
          })
        : [];

    const isDeleted =
        timeTrackingLine.attributes.TaskDetails?.isDeleted || timeTrackingLine.attributes.ProjectDetails?.isDeleted;
    const isArchived =
        timeTrackingLine.attributes.TaskDetails?.isArchived || timeTrackingLine.attributes.ProjectDetails?.isArchived;

    const canClone = entry && !isDeleted && !isArchived && !readonly.global && !readonly.approval && !readonly.projectDisabled;

    const isEntryReadonly = isLocked || readonly.global || readonly.approval || readonly.projectDisabled;

    const commands: ICommandBarItemProps[] = useMemo(() => {
        if (readonly.global) {
            return [];
        }
        if (!entry?.attributes.Duration) {
            return [
                {
                    key: "add",
                    text: "Report Time",
                    iconProps: { iconName: "Add" },
                    disabled: isEntryReadonly || isDeleted || isArchived,
                    onClick: onCreate
                }
            ];
        }

        const canChangeBillable =
            !isEnterpriseTimeTracker || (!!entry?.attributes.Project && timeTrackingSettings.enableEditingBillable);
        const canChangeCostType =
            !!entry?.attributes.Project && timeTrackingSettings.enableEditingCostType && isEnterpriseTimeTracker;

        let cloneItems: IContextualMenuItem[] = [];
        if (canClone) {
            const onEntryClone = (dates: Date[]) => {
                onClone(_getEntriesToClone(entry, dates));
            };

            cloneItems = _buildCloneMenuItems(entry, resourceCalendarDataSet, periods, shownPeriod, onEntryClone);
        }

        return [
            {
                key: "edit",
                text: "Edit",
                iconProps: { iconName: "Edit" },
                disabled: isEntryReadonly,
                onClick: () => onStartEditing(entry)
            },
            canChangeCostType
                ? {
                      key: "changeCostType",
                      iconProps: { iconName: "Money" },
                      text: "Cost Type",
                      subMenuProps: {
                          items: [
                              _buildMarkAsMenuItem(
                                  "costType",
                                  CostType.Capex,
                                  CostType.Capex,
                                  entry,
                                  nameof<TimeTrackingEntryAttr>("CostType"),
                                  onEditCompleted,
                                  isEntryReadonly
                              ),
                              _buildMarkAsMenuItem(
                                  "costType",
                                  CostType.Opex,
                                  CostType.Opex,
                                  entry,
                                  nameof<TimeTrackingEntryAttr>("CostType"),
                                  onEditCompleted,
                                  isEntryReadonly
                              )
                          ]
                      }
                  }
                : undefined,
            canChangeBillable
                ? {
                      key: "changeBillable",
                      iconProps: { iconName: "PPMXMoneyHand" },
                      text: "Billable",
                      subMenuProps: {
                          items: [
                              _buildMarkAsMenuItem(
                                  "billable",
                                  "Yes",
                                  true,
                                  entry,
                                  nameof<TimeTrackingEntryAttr>("IsBillable"),
                                  onEditCompleted,
                                  isEntryReadonly
                              ),
                              _buildMarkAsMenuItem(
                                  "billable",
                                  "No",
                                  false,
                                  entry,
                                  nameof<TimeTrackingEntryAttr>("IsBillable"),
                                  onEditCompleted,
                                  isEntryReadonly
                              )
                          ]
                      }
                  }
                : undefined,
            {
                key: "clone",
                text: "Clone",
                iconProps: { iconName: "Copy" },
                disabled: !cloneItems.length,
                subMenuProps: {
                    items: cloneItems
                }
            }
        ].filter(notUndefined);
    }, [entry, onCreate, onStartEditing, onClone, onEditCompleted, canClone, isEntryReadonly]);

    return (
        <div className="timeline-tooltip time-tracking-tooltip">
            <div className="header">
                {entry?.attributes.AdministrativeCategory ? (
                    <TimeTrackingAdministrativeCategoryEntityName categoryId={entry?.attributes.AdministrativeCategory} />
                ) : (
                    <EntityName
                        name={title}
                        isTimelineView
                        replaceCurrentUrl
                        renderLogo={() => <TaskIcon entity={{ externalData: {} }} />}
                    />
                )}

                {isLocked ? (
                    <>(Locked)</>
                ) : isArchived ? (
                    <>({timeTrackingLine.attributes.IsAdministrative ? "Inactive" : "Archived"})</>
                ) : isDeleted ? (
                    <>(Deleted)</>
                ) : (
                    <></>
                )}
            </div>
            <div className="content">
                {!!entry &&
                    visibleFields.map(field => {
                        return renderTooltipField(
                            field.label || field.name,
                            <DisplayField entity={entry} field={field} viewType="Tooltip" />,
                            field.name.toLowerCase()
                        );
                    })}

                <div className="item align-center menu">
                    <CommandBar
                        className="header-command-bar"
                        items={commands}
                        styles={{ root: { padding: "unset", minWidth: 400 } }}
                    />
                </div>
            </div>
        </div>
    );
}

const _buildMarkAsMenuItem = (
    key: string,
    text: string,
    value: any,
    entry: TimeTrackingEntry,
    attribute: string,
    onEditCompleted: (id: string, attributes: Partial<TimeTrackingEntryAttr>) => void,
    disabled?: boolean
): IContextualMenuItem => {
    return {
        key: key + text,
        text: text,
        iconProps: entry.attributes[attribute] === value ? { iconName: "Accept" } : undefined,
        disabled: disabled,
        onClick: entry.attributes.IsBillable === value ? undefined : () => onEditCompleted(entry.id, { [attribute]: value })
    };
};

const _buildCloneMenuItems = (
    entry: TimeTrackingEntry,
    resourceCalendarDataSet: CalendarDataSet,
    periods: TimeReportingPeriod[],
    period: TimeReportingPeriod | null,
    onClone: (dates: Date[]) => void
): IContextualMenuItem[] => {
    const entryDate = toDateTime(entry.attributes.Date)!;

    const result: IContextualMenuItem[] = [];

    const prevDay = _getPrevWorkingDay(entryDate.clone(), resourceCalendarDataSet, periods);

    if (prevDay) {
        result.push({
            key: "prevDay",
            text: "Previous working day",
            iconProps: {
                iconName: "PPMXArrowLeft"
            },
            onClick: (e, item) => {
                onClone([prevDay]);
            }
        });
    }

    const nextDay = _getNextWorkingDayToClone(entryDate.clone(), resourceCalendarDataSet, periods);

    if (nextDay) {
        result.push({
            key: "nextDay",
            text: "Next working day",
            iconProps: {
                iconName: "PPMXArrowRight"
            },
            onClick: () => {
                onClone([nextDay]);
            }
        });
    }

    if (isPeriodOpenForReporting(period)) {
        const thisPeriod = _getThisPeriodWorkingDaysToClone(
            toDateTime(entry.attributes.Date)!,
            resourceCalendarDataSet,
            periods,
            period!
        );

        if (thisPeriod.length) {
            result.push({
                key: "allPeriod",
                text: "Fill all this period",
                iconProps: {
                    iconName: "CalendarWorkWeek"
                },
                onClick: () => {
                    onClone(thisPeriod);
                }
            });
        }
    }

    const cloneToDate = buildCloneToDateMenuItem(entryDate, periods, date => onClone([date]));

    if (cloneToDate) {
        result.push(cloneToDate);
    }

    return result;
};

const _getPrevWorkingDay = (
    date: Date,
    resourceCalendarDataSet: CalendarDataSet,
    periods: TimeReportingPeriod[]
): Date | undefined => {
    const prevWorkingDay = date.addWorkingDays(-1, resourceCalendarDataSet);

    return isDateInClosedPeriod(prevWorkingDay, periods) ? undefined : prevWorkingDay;
};

const _getNextWorkingDayToClone = (
    date: Date,
    resourceCalendarDataSet: CalendarDataSet,
    periods: TimeReportingPeriod[]
): Date | undefined => {
    const preWorkingDay = date.addWorkingDays(1, resourceCalendarDataSet);

    return isDateInClosedPeriod(preWorkingDay, periods) ? undefined : preWorkingDay;
};

const _getThisPeriodWorkingDaysToClone = (
    date: Date,
    resourceCalendarDataSet: CalendarDataSet,
    periods: TimeReportingPeriod[],
    period: TimeReportingPeriod
): Date[] => {
    const result = getDaysBetweenDates(period.attributes.StartDate, period.attributes.EndDate).filter(
        _ =>
            _.toDateOnlyString() !== date.toDateOnlyString() &&
            _.isWorkingDay(resourceCalendarDataSet) &&
            !isDateInClosedPeriod(_, periods)
    );

    return result;
};

const _getEntriesToClone = (entry: TimeTrackingEntry, dates: Date[]): CreateTimeTrackingEntry[] => {
    return dates.map(_ => {
        return {
            attributes: {
                ...entry.attributes,
                Date: _
            },
            sourceInfo: entry.sourceInfos?.find(si => si.type === TimeTrackingEntrySourceType.ResourcePlaSuggestion)
        };
    });
};

function mapStateToProps(state: ApplicationState, ownProps: OwnProps): StoreProps {
    const timeTrackingEntryFields = state.fields[EntityType.TimeTrackingEntry];

    return {
        fields: timeTrackingEntryFields.allIds.map(_ => timeTrackingEntryFields.byId[_]),
        timeTrackingSettings: state.tenant.timeTracking.globalSettings,
        isEnterpriseTimeTracker: IsEnterpriseTimeTracker(state.tenant.subscription),
        periods: state.timeReportingPeriods.periods,
        shownPeriod: state.timeTracking.shownPeriod
    };
}

export default connect(mapStateToProps)(TimeTrackingCellTooltip);

export const renderTooltipField = (name: string, valueElement: JSX.Element | string, className?: string): JSX.Element => {
    return (
        <div className={`item align-center ${className}`} key={name}>
            <div className="label">{name}</div>
            <div className="value">{valueElement}</div>
        </div>
    );
};

export const isFieldHasValue = (value: any): boolean => {
    if (value === undefined || value === null || value === "" || IsEmptyObject(value)) {
        return false;
    }

    return true;
};
